import type { ReactNode } from 'react';
import { Icon, type IconName } from '@/components/Icon';
import type {
  AlertSeverity,
  AlertState,
  RunStatus,
  RunStepStatus,
  TriggerSpec,
  WorkflowHealth,
} from '@/types/sturm';

type BadgeKey = RunStatus | RunStepStatus | WorkflowHealth | 'retrying';

const BADGES: Record<string, { cls: string; text: string }> = {
  completed: { cls: 'badge-success', text: 'Completed' },
  failed: { cls: 'badge-error', text: 'Failed' },
  running: { cls: 'badge-info badge-running', text: 'Running' },
  retrying: { cls: 'badge-warn', text: 'Retrying' },
  timedout: { cls: 'badge-warn', text: 'Timed out' },
  terminated: { cls: 'badge-neutral', text: 'Terminated' },
  cancelled: { cls: 'badge-neutral', text: 'Cancelled' },
  queued: { cls: 'badge-neutral', text: 'Queued' },
  waiting: { cls: 'badge-neutral', text: 'Waiting' },
  skipped: { cls: 'badge-neutral', text: 'Skipped' },
  healthy: { cls: 'badge-success', text: 'Healthy' },
  degraded: { cls: 'badge-warn', text: 'Degraded' },
  failing: { cls: 'badge-error', text: 'Failing' },
  paused: { cls: 'badge-neutral', text: 'Paused' },
  disabled: { cls: 'badge-neutral', text: 'Disabled' },
};

export function StatusBadge({ status, label }: { status: BadgeKey | string; label?: string }) {
  const v = BADGES[status] ?? { cls: 'badge-neutral', text: status };
  return (
    <span className={`badge ${v.cls}`}>
      <span className="dot" />
      {label ?? v.text}
    </span>
  );
}

export function SeverityBadge({ severity }: { severity: AlertSeverity }) {
  const cls =
    severity === 'critical' ? 'badge-error' : severity === 'warning' ? 'badge-warn' : 'badge-info';
  const text = severity[0].toUpperCase() + severity.slice(1);

  return (
    <span className={`badge ${cls}`}>
      <span className="dot" />
      {text}
    </span>
  );
}

export function StatePill({ state, by }: { state: AlertState; by?: string | null }) {
  const label =
    state === 'acknowledged' && by ? `Acked · ${by}` : state[0].toUpperCase() + state.slice(1);

  return <span className={`state-pill state-${state}`}>{label}</span>;
}

export function SparkRuns({ statuses, height = 22 }: { statuses: RunStatus[]; height?: number }) {
  return (
    <div className="spark" style={{ height }}>
      {statuses.map((s, i) => (
        <i key={i} className={`sp-${s}`} />
      ))}
    </div>
  );
}

const TRIGGER_ICON: Record<string, IconName> = {
  schedule: 'clock',
  event: 'bolt',
  api: 'link',
  manual: 'play',
  retry: 'refresh',
};

export function TriggerChip({ trigger }: { trigger: TriggerSpec }) {
  const label =
    trigger.kind === 'schedule' || trigger.kind === 'event'
      ? (trigger.detail ?? trigger.kind)
      : trigger.kind === 'api'
        ? 'API'
        : trigger.kind[0].toUpperCase() + trigger.kind.slice(1);

  return (
    <span className={`wf-kind k-${trigger.kind}`}>
      <Icon name={TRIGGER_ICON[trigger.kind] ?? 'link'} size={11} />
      {label}
    </span>
  );
}

export function TestModeChip() {
  return (
    <span className="wf-kind k-test" title="Test mode: reads and checks credentials, writes nothing">
      <Icon name="eye" size={11} />
      Test
    </span>
  );
}

const KNOWN_LABELS: Record<string, { className: string; icon: IconName; title: string }> = {
  obsolete: {
    className: 'k-obsolete',
    icon: 'alert',
    title: 'Obsolete: kept for comparison, scheduled for removal',
  },
};

export function WorkflowLabelChip({ label }: { label: string }) {
  const known = KNOWN_LABELS[label];

  return (
    <span className={`wf-kind ${known?.className ?? ''}`} title={known?.title ?? `Label: ${label}`}>
      <Icon name={known?.icon ?? 'tag'} size={11} />
      {label}
    </span>
  );
}

export function WorkflowLabelChips({ labels }: { labels: string[] }) {
  return (
    <>
      {labels.map((label) => (
        <WorkflowLabelChip key={label} label={label} />
      ))}
    </>
  );
}

export function Switch({
  on,
  onChange,
  label,
  disabled,
  small,
}: {
  on: boolean;
  onChange: (next: boolean) => void;
  label: string;
  disabled?: boolean;
  small?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      className={`switch ${small ? 'sm' : ''} ${on ? 'on' : ''}`}
      onClick={() => onChange(!on)}
      disabled={disabled}
    >
      <span className="knob" />
    </button>
  );
}

export function CodeChip({ icon, children }: { icon: IconName; children: ReactNode }) {
  return (
    <span className="code-chip">
      <Icon name={icon} size={12} />
      {children}
    </span>
  );
}

export function Stat({
  icon,
  label,
  value,
  meta,
  tone,
  onClick,
}: {
  icon: IconName;
  label: string;
  value: ReactNode;
  meta?: ReactNode;
  tone?: 'error';
  onClick?: () => void;
}) {
  return (
    <div className={`stat ${onClick ? 'clickable-stat' : ''}`} onClick={onClick}>
      <div className="stat-label">
        <Icon name={icon} size={13} /> {label}
      </div>
      <div className="stat-value" style={tone === 'error' ? { color: 'var(--error)' } : undefined}>
        {value}
      </div>
      {meta ? <div className="stat-meta">{meta}</div> : null}
    </div>
  );
}

export function Dim({ children }: { children: ReactNode }) {
  return <span className="muted" style={{ fontSize: 15, fontWeight: 500 }}>{children}</span>;
}

export function EmptyState({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="empty">
      <div className="empty-title">{title}</div>
      {sub ? <div className="empty-sub">{sub}</div> : null}
    </div>
  );
}

export function Banner({ children, tone }: { children: ReactNode; tone?: 'warn' }) {
  return (
    <div className={`banner ${tone ?? ''}`}>
      <Icon name={tone === 'warn' ? 'alert' : 'info'} size={16} />
      <div>{children}</div>
    </div>
  );
}

export function SectionTitle({ children, count, right }: { children: ReactNode; count?: ReactNode; right?: ReactNode }) {
  return (
    <div className="section-title">
      {children}
      {count != null ? <span className="count">{count}</span> : null}
      {right ? <span className="right">{right}</span> : null}
    </div>
  );
}

export function Spinner({ size = 16 }: { size?: number }) {
  return <Icon name="refresh" size={size} className="spin" />;
}

export function LoadingPage({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="page">
      <div className="row gap-8 muted">
        <Spinner /> {label}
      </div>
    </div>
  );
}

export function ErrorPage({ title, message }: { title: string; message: string }) {
  return (
    <div className="page">
      <div className="card">
        <div className="card-hd" style={{ background: 'var(--error-tint)', borderColor: 'var(--error-soft)' }}>
          <Icon name="alert" size={15} style={{ color: 'var(--error)' }} />
          <div className="flex1">
            <div className="card-hd-title" style={{ color: 'var(--error)' }}>
              {title}
            </div>
          </div>
        </div>
        <div className="card-pad">
          <div className="json">{message}</div>
        </div>
      </div>
    </div>
  );
}
